import React, { type ComponentProps, useEffect } from 'react';
import {
  defineMessages,
  type FormatNumberOptions,
  FormattedMessage,
  type MessageValue,
  type NoMessageValues,
  useIntl,
} from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { AnimatedNumberText, BodyText, Icon, theme, useResponsive } from '@sg/garnish';

import {
  BUNDLE_FOOTER_BUTTON_HEIGHT,
  BUNDLE_FOOTER_BUTTON_HEIGHT_XS,
} from '../../Bundle.constants';
import { type BundleAddToBagStatus } from '../../Bundle.types';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleAddToBagButton = (props: Props) => {
  const { status, settledCount, totalCount, totalCost, disabled = false, onPress } = props;

  const { formatMessage, formatNumber } = useIntl();
  const { currentBreakpoint } = useResponsive();

  // ─── Refs ────────────────────────────────────────────────────────────

  const labelPosition = useSharedValue(LABEL_POSITIONS[status]);
  const fillProgress = useSharedValue(getFillProgress({ status, settledCount, totalCount }));

  // ─── Derived ─────────────────────────────────────────────────────────

  const isAdding = status === 'adding';
  const isPressDisabled = disabled || status !== 'idle';
  const hasDisabledLook = disabled && status === 'idle';
  const hasTotalCost = totalCost > 0;
  const formattedTotalCost = formatNumber(totalCost / CENTS_IN_DOLLAR, CURRENCY_FORMAT);
  const addingPosition = getAddingPosition({ settledCount, totalCount });
  const accessibilityState = { disabled: isPressDisabled, busy: isAdding };
  const accessibilityLabels: Record<BundleAddToBagStatus, string> = {
    idle: hasTotalCost
      ? formatMessage(messages.addToBagWithPrice, { price: formattedTotalCost })
      : formatMessage(messages.addToBag),
    adding: formatMessage(messages.addingProgress, { position: addingPosition, total: totalCount }),
    added: formatMessage(messages.added),
  };

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    labelPosition.value = withTiming(LABEL_POSITIONS[status], LABEL_TIMING_CONFIG);
  }, [labelPosition, status]);

  useEffect(() => {
    const targetFillProgress = getFillProgress({ status, settledCount, totalCount });

    fillProgress.value = withTiming(targetFillProgress, FILL_TIMING_CONFIG);
  }, [fillProgress, settledCount, status, totalCount]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const fillAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${(fillProgress.value - 1) * 100}%` as const }],
  }));

  // NOTE: Every row fills the button, so each step moves the reel by its full height.
  const labelReelAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: `${-labelPosition.value * 100}%` as const }],
  }));

  const containerStyles = [
    styles.container,
    currentBreakpoint.isXS ? styles.containerXS : undefined,
    hasDisabledLook ? styles.containerDisabled : undefined,
  ];
  const fillStyles = [styles.fill, fillAnimatedStyle];
  const labelReelStyles = [styles.labelReel, labelReelAnimatedStyle];
  const labelStyles = [styles.label, hasDisabledLook ? styles.labelDisabled : undefined];
  const addingRowStyles = [styles.labelRow, styles.addingLabelRow];
  const addedRowStyles = [styles.labelRow, styles.addedLabelRow];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityLabel={accessibilityLabels[status]}
      accessibilityRole="button"
      accessibilityState={accessibilityState}
      activeOpacity={ACTIVE_OPACITY}
      disabled={isPressDisabled}
      style={containerStyles}
      onPress={onPress}
    >
      <Animated.View style={fillStyles} />

      <Animated.View style={labelReelStyles}>
        <View style={styles.labelRow}>
          <BodyText sizeMatch={LABEL_SIZE_MATCH} style={labelStyles}>
            {hasTotalCost ? (
              <FormattedMessage
                {...messages.addToBagWithPrice}
                values={{ price: formattedTotalCost }}
              />
            ) : (
              <FormattedMessage {...messages.addToBag} />
            )}
          </BodyText>
        </View>

        <View style={addingRowStyles}>
          <BodyText sizeMatch={LABEL_SIZE_MATCH} style={labelStyles}>
            <FormattedMessage {...messages.adding} />
          </BodyText>

          <AnimatedNumberText.BodyText
            text={String(addingPosition)}
            animateInitialValue={false}
            duration={COUNT_ANIMATION_DURATION}
            sizeMatch={LABEL_SIZE_MATCH}
            style={labelStyles}
            containerStyle={styles.countContainer}
          />

          <BodyText sizeMatch={LABEL_SIZE_MATCH} style={labelStyles}>
            <FormattedMessage {...messages.addingTotal} values={{ total: totalCount }} />
          </BodyText>
        </View>

        <View style={addedRowStyles}>
          <Icon
            name="IconCheck2"
            width={CHECKMARK_SIZE}
            height={CHECKMARK_SIZE}
            color={theme.colors.WHITE}
          />
        </View>
      </Animated.View>
    </TouchableOpacity>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getFillProgress(params: GetFillProgressParams) {
  const { status, settledCount, totalCount } = params;

  if (status === 'added') {
    return 1;
  }

  const hasProducts = totalCount > 0;

  if (status === 'adding' && hasProducts) {
    return getAddingPosition({ settledCount, totalCount }) / totalCount;
  }

  return 0;
}

/**
 * The product underway, counted from 1. Stays on the last one once every
 * product settles.
 */
function getAddingPosition(params: GetAddingPositionParams) {
  const { settledCount, totalCount } = params;

  return Math.min(settledCount + 1, totalCount);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTIVE_OPACITY = 0.8;

const LABEL_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['18'];
const LABEL_TIMING_CONFIG = { duration: 600, easing: Easing.out(Easing.cubic) };

/**
 * Rows of the label reel, top to bottom. The reel slides up as the add moves
 * forward, and back down when it has to start over.
 */
const LABEL_POSITIONS: Record<BundleAddToBagStatus, number> = {
  idle: 0,
  adding: 1,
  added: 2,
};

const FILL_TIMING_CONFIG = { duration: 800, easing: Easing.out(Easing.cubic) };
const COUNT_ANIMATION_DURATION = 800;

const CHECKMARK_SIZE = 28;

const CENTS_IN_DOLLAR = 100;
const CURRENCY_FORMAT: FormatNumberOptions = { style: 'currency', currency: 'USD' };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    height: BUNDLE_FOOTER_BUTTON_HEIGHT,
    overflow: 'hidden',
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.KALE,
  },
  containerXS: {
    height: BUNDLE_FOOTER_BUTTON_HEIGHT_XS,
  },
  containerDisabled: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  fill: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.SPINACH,
  },
  labelReel: {
    ...StyleSheet.absoluteFill,
  },
  labelRow: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  addingLabelRow: {
    top: '100%',
    flexDirection: 'row',
    gap: theme.spacing['1'],
  },
  addedLabelRow: {
    top: '200%',
  },

  // NOTE: Native layout caps a column's digit measurement at the digit shown
  //       first, so a narrow 1 would clip every wider digit after it.
  countContainer: {
    flexDirection: 'row',
  },
  label: {
    color: theme.colors.WHITE,
    textAlign: 'center',
  },
  labelDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly addToBag: NoMessageValues;
  readonly addToBagWithPrice: { readonly price: MessageValue };
  readonly adding: NoMessageValues;
  readonly addingTotal: { readonly total: MessageValue };
  readonly addingProgress: { readonly position: MessageValue; readonly total: MessageValue };
  readonly added: NoMessageValues;
}>({
  addToBag: {
    defaultMessage: 'Add to bag',
    description: 'Bundle | Footer | Add to bag button',
  },
  addToBagWithPrice: {
    defaultMessage: 'Add to bag - {price}',
    description: 'Bundle | Footer | Add to bag button with the price of the selected products',
  },
  adding: {
    defaultMessage: 'Adding',
    description: 'Bundle | Footer | Add to bag button text before the product count',
  },
  addingTotal: {
    defaultMessage: 'of {total}',
    description: 'Bundle | Footer | Add to bag button text after the product count',
  },
  addingProgress: {
    defaultMessage: 'Adding {position} of {total}',
    description: 'Bundle | Footer | Add to bag button label while the products are being added',
  },
  added: {
    defaultMessage: 'Added to bag',
    description: 'Bundle | Footer | Add to bag button label once the products are added',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  status: BundleAddToBagStatus;

  /**
   * Products that reported back in the current add, whether or not they were
   * added.
   */
  settledCount: number;
  totalCount: number;

  /**
   * Price of the selected products, in cents. Left off the label while 0.
   */
  totalCost: number;
  disabled?: boolean;
  onPress: () => void;
};

type GetFillProgressParams = Pick<Props, 'status' | 'settledCount' | 'totalCount'>;
type GetAddingPositionParams = Pick<Props, 'settledCount' | 'totalCount'>;
